import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useBoolean } from 'react-use'

import { NotificationsAPI } from '@makyoapp/makyo-sdk'
import { Order } from '@makyoapp/makyo-sdk/api/api'
import { NotificationOrderBy } from '@makyoapp/makyo-sdk/models/Notifications'
import { fromSdkNotificationList, NotificationItemAdapter } from '@makyoapp/makyo-common'

import { useDeferRequests } from '@/hooks/useDeferRequests'

import useAuth from '../hooks/useAuth'
import { useReqError } from '../hooks/useReqError'

interface NotificationsState {
  notifications: NotificationItemAdapter[]
  setNotifications: (nextItems: NotificationItemAdapter[]) => void
  unreadCount: number
  setUnreadCount: (nextUnread: number) => void
  isLoading: boolean
  setIsLoading: (nextLoading: boolean) => void
  updateRead: (notificationIds: string[]) => [number, NotificationItemAdapter[]]
  updateUnread: (notificationIds: string[]) => [number, NotificationItemAdapter[]]
  update: () => void
}

const NotificationsContext = createContext<NotificationsState>({
  notifications: [],
  setNotifications: () => null,
  unreadCount: 0,
  setUnreadCount: () => null,
  isLoading: false,
  setIsLoading: () => null,
  updateRead: () => [0, []],
  updateUnread: () => [0, []],
  update: () => null,
})

/**
 * use app context hook
 */
export function useNotificationsContext() {
  return useContext(NotificationsContext)
}

interface NotificationsProviderProps {
  children?: React.ReactNode
}

/**
 * Notification context provider
 * primarily used by toolbar
 *
 */
export const NotificationsProvider = ({ children }: NotificationsProviderProps) => {
  const { userData } = useAuth()
  const printAPIError = useReqError()

  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useBoolean(false)
  const [notifications, setNotifications] = useState<NotificationItemAdapter[]>([])

  const { shouldFetch } = useDeferRequests()

  const handleList = useCallback(() => {
    setIsLoading(true)
    NotificationsAPI.ReqList(1, Order.Desc, NotificationOrderBy.CreatedAt)
      .then((res) => {
        const fromSdk = fromSdkNotificationList(res._Data)
        setUnreadCount(res._NotReadedCount)
        setNotifications(fromSdk)
      })
      .catch((e) => {
        printAPIError(e)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [setUnreadCount, setNotifications, setIsLoading, printAPIError])

  useEffect(() => {
    if (userData && shouldFetch) {
      handleList()
    }
  }, [handleList, userData, shouldFetch])

  const updateRead = (notificationId: string[]): [number, NotificationItemAdapter[]] => {
    let count = 0
    const newNt = [...notifications]
    for (const nId of notificationId) {
      const idx = newNt.findIndex((i) => i.id === nId)
      if (idx !== -1) {
        count += 1
        newNt[idx].isRead = true
      }
    }
    setNotifications(newNt)
    return [count, newNt]
  }

  const updateUnread = (notificationId: string[]): [number, NotificationItemAdapter[]] => {
    let count = 0
    const newNt = [...notifications]
    for (const nId of notificationId) {
      const idx = newNt.findIndex((i) => i.id === nId)
      if (idx !== -1) {
        count += 1
        newNt[idx].isRead = false
      }
    }
    setNotifications(newNt)
    return [count, newNt]
  }

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        setNotifications,
        unreadCount,
        setUnreadCount,
        isLoading,
        setIsLoading,
        updateRead,
        updateUnread,
        update: handleList,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  )
}
